import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { meteo as meteoAPI } from './api';

function Meteo() {
  const navigate = useNavigate();
  const [zones, setZones] = useState([]);
  const [selectedZone, setSelectedZone] = useState('manche');
  const [meteoData, setMeteoData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [gpsPosition, setGpsPosition] = useState(null);

  // Coordonnées Windy selon la zone
  const windyCoords = {
    manche: { lat: 50, lng: -1, zoom: 6 },
    atlantique: { lat: 46, lng: -4, zoom: 6 },
    mediterranee: { lat: 43, lng: 6, zoom: 6 },
    caraibes: { lat: 15.5, lng: -61.5, zoom: 7 }
  };

  useEffect(() => {
    loadZones();
    detectZoneFromGPS();
  }, []);

  useEffect(() => {
    if (selectedZone) {
      loadMeteo(selectedZone);
    }
  }, [selectedZone]);

  const loadZones = async () => {
    try {
      const response = await meteoAPI.getZones();
      setZones(response.data);
    } catch (err) {
      console.error('Erreur chargement zones:', err);
    }
  };

  const detectZoneFromGPS = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const lat = position.coords.latitude;
          const lng = position.coords.longitude;
          setGpsPosition({ lat, lng });

          try {
            const response = await meteoAPI.detectZone(lat, lng);
            setSelectedZone(response.data.zone);
          } catch (err) {
            console.error('Erreur détection zone:', err);
          }
        },
        (error) => {
          console.log('GPS non disponible, zone par défaut:', error);
        }
      );
    }
  };

  const loadMeteo = async (zone) => {
    setLoading(true);
    try {
      const response = await meteoAPI.get(zone);
      setMeteoData(response.data);
    } catch (err) {
      console.error('Erreur chargement météo:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleZoneChange = (e) => {
    setSelectedZone(e.target.value);
  };

  const coords = windyCoords[selectedZone] || windyCoords.manche;
  const windyUrl = `https://embed.windy.com/embed2.html?lat=${coords.lat}&lon=${coords.lng}&detailLat=${coords.lat}&detailLon=${coords.lng}&width=650&height=450&zoom=${coords.zoom}&level=surface&overlay=wind&product=ecmwf&menu=&message=&marker=&calendar=now&pressure=&type=map&location=coordinates&detail=&metricWind=kt&metricTemp=%C2%B0C&radarRange=-1`;

  return (
    <div className="meteo-page">
      <div className="meteo-header">
        <h1>🌊 Météo Marine</h1>
        <select value={selectedZone} onChange={handleZoneChange} className="zone-selector">
          {zones.map(zone => (
            <option key={zone.id} value={zone.id}>
              {zone.name}
            </option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="loading">Chargement de la météo...</div>
      ) : meteoData ? (
        <>
          <div className="meteo-info">
            <div className="info-section">
              <h2>📰 Situation générale</h2>
              <p className="situation">{meteoData.situation}</p>
            </div>

            <div className="info-grid">
              <div className="info-card">
                <h3>💨 Vent</h3>
                <p><strong>{meteoData.vent.force}</strong></p>
                <p>{meteoData.vent.direction}</p>
                <p className="beaufort">Beaufort {meteoData.vent.beaufort}</p>
              </div>

              <div className="info-card">
                <h3>🌊 Mer</h3>
                <p><strong>{meteoData.mer.etat}</strong></p>
                <p>Hauteur: {meteoData.mer.hauteur}</p>
                <p>Houle: {meteoData.mer.houle}</p>
              </div>

              <div className="info-card">
                <h3>👁️ Visibilité</h3>
                <p><strong>{meteoData.visibilite}</strong></p>
              </div>
            </div>

            {meteoData.timestamp && (
              <p className="timestamp">
                Dernière mise à jour : {new Date(meteoData.timestamp).toLocaleString('fr-FR')}
              </p>
            )}
          </div>

          <div className="windy-section">
            <h2>🗺️ Carte interactive Windy</h2>
            <div className="windy-container">
              <iframe
                src={windyUrl}
                frameBorder="0"
                title="Carte Windy"
              ></iframe>
            </div>
            <p className="windy-info">
              Utilisez les contrôles Windy pour voir : Vent • Vagues • Température • Courants
            </p>
          </div>
        </>
      ) : (
        <div className="error">Impossible de charger la météo</div>
      )}

      <div className="back-button">
        <button onClick={() => navigate('/')} className="btn-secondary">
          ← Retour au dashboard
        </button>
      </div>

      <style>{`
        .meteo-page {
          padding: 20px;
          max-width: 1200px;
          margin: 0 auto;
        }

        .meteo-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 30px;
          padding: 20px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 12px;
        }

        .meteo-header h1 {
          margin: 0;
          font-size: 2rem;
        }

        .zone-selector {
          padding: 10px 20px;
          font-size: 16px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          background: white;
          color: #333;
        }

        .loading, .error {
          text-align: center;
          padding: 40px;
          font-size: 1.2rem;
          color: #666;
        }

        .meteo-info {
          background: white;
          padding: 30px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin-bottom: 30px;
        }

        .info-section h2 {
          color: #667eea;
          margin-bottom: 15px;
        }

        .situation {
          font-size: 1.1rem;
          line-height: 1.6;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
          border-left: 4px solid #667eea;
        }

        .info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-top: 30px;
        }

        .info-card {
          padding: 20px;
          background: #f8f9fa;
          border-radius: 8px;
          text-align: center;
        }

        .info-card h3 {
          color: #667eea;
          margin-bottom: 15px;
        }

        .info-card p {
          margin: 8px 0;
          font-size: 1rem;
        }

        .beaufort {
          color: #764ba2;
          font-weight: bold;
        }

        .timestamp {
          text-align: center;
          color: #999;
          font-size: 0.9rem;
          margin-top: 20px;
        }

        .windy-section {
          background: white;
          padding: 30px;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          margin-bottom: 30px;
        }

        .windy-section h2 {
          color: #667eea;
          margin-bottom: 20px;
        }

        .windy-container {
          position: relative;
          padding-bottom: 56.25%;
          height: 0;
          overflow: hidden;
          border-radius: 8px;
        }

        .windy-container iframe {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
        }

        .windy-info {
          text-align: center;
          color: #666;
          font-size: 0.95rem;
          margin-top: 15px;
        }

        .back-button {
          text-align: center;
          margin-top: 30px;
        }

        .btn-secondary {
          padding: 12px 24px;
          background: #6c757d;
          color: white;
          border: none;
          border-radius: 6px;
          font-size: 16px;
          cursor: pointer;
          transition: all 0.3s;
        }

        .btn-secondary:hover {
          background: #545b62;
        }
      `}</style>
    </div>
  );
}

export default Meteo;